<script setup lang="ts">
import { ApiError } from '@/utils/errors';
import { onMounted, shallowRef } from 'vue';
import { useCommonApiService } from '@/modules/common/services/common.api.service';
import { useDialogManager } from '@/plugins/dialog-manager';

const commonApiService = useCommonApiService();
const dialogManager = useDialogManager();

const fetchStatus = shallowRef<'fetching' | 'completed' | 'failed'>('fetching');
const fetchError = shallowRef('');
const isKillSwitchOn = shallowRef(false);
const isHaltTradingOn = shallowRef(false);

function toggleKillSwitch(): void {
  // confirm is green button, alert is red button
  const dialogMethod = isKillSwitchOn.value ? 'confirm' : 'alert';

  dialogManager[dialogMethod]({
    title: 'Are you sure?',
    message: `You are about to ${isKillSwitchOn.value ? 'enable' : 'disable'} the Marketplace`,
    isRejectable: true,
    rejectText: 'Cancel',
    acceptText: isKillSwitchOn.value ? 'Enable Marketplace' : 'Disable Marketplace',
    onAccept: async () => {
      await commonApiService.updateKillSwitch(isKillSwitchOn.value ? 'disable' : 'enable');
      isKillSwitchOn.value = !isKillSwitchOn.value;

      if (isKillSwitchOn.value) {
        await commonApiService.updateHaltTrading('enable');
        isHaltTradingOn.value = true;
      }
    },
    shouldManuallyConfirm: true,
  });
}

function toggleHaltTrading(): void {
  // confirm is green button, alert is red button
  const dialogMethod = isHaltTradingOn.value ? 'confirm' : 'alert';

  dialogManager[dialogMethod]({
    title: 'Are you sure?',
    message: `You are about to ${isHaltTradingOn.value ? 'enable' : 'disable'} trading`,
    isRejectable: true,
    rejectText: 'Cancel',
    acceptText: isHaltTradingOn.value ? 'Enable Trading' : 'Disable Trading',
    onAccept: async () => {
      await commonApiService.updateHaltTrading(isHaltTradingOn.value ? 'disable' : 'enable');
      isHaltTradingOn.value = !isHaltTradingOn.value;
    },
    shouldManuallyConfirm: true,
  });
}

function reloadPage(): void {
  window.location.reload();
}

onMounted(async () => {
  try {
    const data = await commonApiService.fetchKillSwitchState();
    fetchStatus.value = 'completed';

    isKillSwitchOn.value = data.isKillSwitchOn;
    isHaltTradingOn.value = data.isHaltTradingOn;
  } catch (err) {
    fetchStatus.value = 'failed';
    fetchError.value = (err as ApiError).message;
  }
});
</script>

<template>
  <div class="px-6 py-2">
    <VRow v-if="fetchStatus === 'fetching'" class="justify-center pt-16 flex-column align-center">
      <FulfillingBouncingCircleSpinner :animation-duration="1500" color="#4caf50" :size="50" />
      <h3 class="mt-2">Fetching killswitch details...</h3>
    </VRow>

    <VRow
      v-else-if="fetchStatus === 'failed'"
      class="justify-center pt-16 flex-column align-center"
    >
      <VAlert dense type="error">
        {{ fetchError }}
      </VAlert>
      <VBtn color="primary mt-4" @click="reloadPage()">Try again</VBtn>
    </VRow>

    <VRow v-else-if="fetchStatus === 'completed'">
      <VCol cols="12">
        <VSwitch
          color="red"
          data-test="kill-switch-switch"
          :input-value="isKillSwitchOn"
          :label="isKillSwitchOn ? 'Marketplace shut down' : 'Marketplace operational'"
          readonly
          @click="toggleKillSwitch"
        />
        <VAlert outlined text type="error">
          When Marketplace is disabled through killswitch:
          <ul class="mt-2">
            <li>All Marketplace operations (including trading) are disabled</li>
            <li>All routed venue orders are canceled</li>
          </ul>
        </VAlert>

        <VDivider class="mt-8" />
        <VSwitch
          :class="{ 'halt-trading-disabled': isKillSwitchOn }"
          color="red"
          data-test="trading-switch"
          :disabled="isKillSwitchOn"
          :input-value="isHaltTradingOn"
          :label="isHaltTradingOn ? 'Trading shut down' : 'Trading operational'"
          readonly
          @click="toggleHaltTrading"
        />
        <VAlert outlined text type="error">
          When Trading is disabled through killswitch:
          <ul class="mt-2">
            <li>Users are <strong>not allowed</strong> to create or modify orders</li>
            <li>Users can still cancel and unroute orders</li>
          </ul>
        </VAlert>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss" scoped>
.halt-trading-disabled {
  pointer-events: none;
}
</style>
